import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Shield, Mail, Phone, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import SharedHeader from "@/components/SharedHeader";
import SharedFooter from "@/components/SharedFooter";

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <SharedHeader 
          transparent={true}
          rightContent={
            <Link to="/">
              <Button variant="ghost">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          }
        />
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-10">
          
          {/* Last Updated */}
          <p className="text-sm text-gray-500 mb-6">
            Last Updated: April 8, 2025
          </p>

          {/* Introduction */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Introduction</h2>
            <p className="text-gray-700 leading-relaxed">
              Eduzy ("we," "our," or "us") is committed to protecting the privacy of children 
              and their families. This Privacy Policy explains how we collect, use, store, and 
              protect personal data in compliance with the <strong>Digital Personal Data Protection 
              Act, 2023 (DPDP Act)</strong> of India.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              Our platform is designed for students in grades 5-8 (ages 10-14). Since our users 
              are children under 18 years of age, we require <strong>verifiable parental consent</strong> before 
              collecting or processing any personal data.
            </p>
          </section>

          {/* Data We Collect */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Data We Collect</h2>
            
            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Parent/Guardian Information</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
              <li>Full name</li>
              <li>Phone number (for verification and communication)</li>
              <li>Relationship to child</li>
              <li>Consent records and timestamps</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Student Information</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
              <li>Full name</li>
              <li>Age and grade level</li>
              <li>Subjects of interest</li>
              <li>Learning progress and quiz scores</li>
              <li>Questions asked during learning sessions</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Technical Information</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
              <li>WhatsApp phone number</li>
              <li>Session timestamps</li>
              <li>Device information (for security purposes only)</li>
            </ul>
          </section>

          {/* How We Use Data */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">How We Use Your Data</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We use the collected data solely for educational purposes:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Personalized Learning:</strong> Generate age-appropriate educational content based on grade level</li>
              <li><strong>Progress Tracking:</strong> Monitor learning progress and provide performance insights</li>
              <li><strong>Communication:</strong> Send educational content and respond to questions via WhatsApp</li>
              <li><strong>Service Improvement:</strong> Improve our educational content and platform features</li>
              <li><strong>Parental Updates:</strong> Provide parents with reports on their child's learning progress</li>
            </ul>
          </section>

          {/* What We Don't Do */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">What We Do NOT Do</h2>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-gray-700 leading-relaxed">
                In compliance with Section 9 of the DPDP Act, we <strong>strictly prohibit</strong>:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-2">
                <li>❌ Tracking or cross-site monitoring of children</li>
                <li>❌ Behavioral profiling or psychological analysis</li>
                <li>❌ Targeted advertising to children</li>
                <li>❌ Selling or sharing data with third-party advertisers</li>
                <li>❌ Any processing that may cause harm to the child</li>
              </ul>
            </div>
          </section>

          {/* Parental Consent */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Parental Consent</h2>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <p className="text-gray-700 leading-relaxed">
                <strong>Verifiable parental consent is mandatory</strong> before we collect any data from children.
              </p>
              <p className="text-gray-700 leading-relaxed mt-2">
                Our consent process includes:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4 mt-2">
                <li>Parent/guardian must initiate the registration</li>
                <li>OTP verification sent to parent's registered email address</li>
                <li>Clear disclosure of data collection and usage</li>
                <li>Explicit consent acknowledgment before child registration</li>
              </ul>
            </div>
          </section>

          {/* Verification & Security OTPs */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Verification & Security OTPs</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              To protect your child's data and account, we use One-Time Password (OTP) verification for sensitive actions:
            </p>
            
            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Registration OTP</h3>
            <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
              <li>Sent to the parent's registered email address during initial registration</li>
              <li>Valid for 10 minutes</li>
              <li>Required to complete the registration process</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Post-Registration Verification OTPs</h3>
            <p className="text-gray-700 mb-2">After registration, verification OTPs can be sent to:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
              <li>Registered WhatsApp number</li>
              <li>Registered email address</li>
            </ul>
            <p className="text-gray-700 mt-2">These OTPs are required for:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
              <li>Account modifications or updates</li>
              <li>Password changes</li>
              <li>Consent withdrawal</li>
              <li>Account deletion requests</li>
              <li>Admin-initiated changes (parent must provide OTP to admin)</li>
            </ul>
          </section>

          {/* Account Modifications and Deletions */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Account Modifications and Deletions</h2>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <p className="text-gray-700 leading-relaxed font-medium">
                <strong>Important:</strong> All account modifications and deletions require OTP verification from the parent.
              </p>
            </div>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Parent-Initiated Changes</h3>
            <p className="text-gray-700 mb-2">When you request changes to your child's account:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
              <li>A verification OTP is sent to your registered email/WhatsApp</li>
              <li>You must enter the OTP to authorize the change</li>
              <li>For deletions, you will receive a warning that the action is <strong>unrecoverable</strong></li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Admin-Initiated Changes</h3>
            <p className="text-gray-700 mb-2">If you request an admin to modify or delete records:</p>
            <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
              <li>A verification OTP is sent to your registered email/WhatsApp</li>
              <li>You must call our support line (+91 9654006470) and provide the OTP to the admin</li>
              <li>The admin cannot make any changes without your verified OTP</li>
              <li>This protects against unauthorized modifications</li>
            </ul>

            <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Account Deletion Warning</h3>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-700 leading-relaxed">
                <strong>Account deletion is UNRECOVERABLE.</strong> When you delete an account, the following data 
                will be permanently removed and cannot be restored:
              </p>
              <ul className="list-disc list-inside text-red-700 space-y-1 ml-4 mt-2">
                <li>Student profile and personal information</li>
                <li>All learning progress and statistics</li>
                <li>Assessment history and scores</li>
                <li>Chat history and interactions</li>
                <li>Subscription records</li>
              </ul>
            </div>
          </section>

          {/* Audit Logging */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Audit Logging</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              For security, compliance, and dispute resolution, we maintain audit logs of all significant actions:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Actions Logged:</strong> Account creation, modifications, deletions, login attempts, 
                OTP verifications, consent changes, and admin operations</li>
              <li><strong>Information Recorded:</strong> Timestamp, action type, user/admin identifier, 
                IP address (for security), and whether parent notification/OTP verification occurred</li>
              <li><strong>Retention Period:</strong> Audit logs are retained for <strong>180 days</strong> 
                as required for DPDP Act compliance and dispute resolution</li>
              <li><strong>Access:</strong> Audit logs are accessible only to authorized administrators 
                and can be provided upon legal request</li>
            </ul>
          </section>

          {/* Data Storage and Security */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Storage and Security</h2>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Encryption:</strong> All data is encrypted in transit (HTTPS/TLS) and at rest</li>
              <li><strong>Password Security:</strong> Passwords are hashed using bcrypt algorithm</li>
              <li><strong>Access Control:</strong> Role-based access ensures only authorized personnel can access data</li>
              <li><strong>Data Location:</strong> All data is stored on secure servers in India</li>
              <li><strong>Retention Period:</strong> Data is retained only as long as necessary for educational purposes or as required by law</li>
              <li><strong>Audit Logs:</strong> All changes and deletions are logged and retained for 180 days</li>
            </ul>
          </section>

          {/* Your Rights */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Rights (Data Principal Rights)</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Under the DPDP Act, parents/guardians have the following rights:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-800">Right to Access</h4>
                <p className="text-sm text-gray-600 mt-1">Request a copy of all data we hold about your child</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-800">Right to Correction</h4>
                <p className="text-sm text-gray-600 mt-1">Request corrections to inaccurate or incomplete data</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-800">Right to Erasure</h4>
                <p className="text-sm text-gray-600 mt-1">Request deletion of your child's personal data</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-800">Right to Withdraw Consent</h4>
                <p className="text-sm text-gray-600 mt-1">Withdraw consent at any time; we will stop processing and delete data</p>
              </div>
            </div>
          </section>

          {/* How to Exercise Rights */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">How to Exercise Your Rights</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              To exercise any of your data rights, you can:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Via WhatsApp:</strong> Send "MY DATA" or "DELETE MY DATA" to our WhatsApp number</li>
              <li><strong>Via Parent Dashboard:</strong> Log in to view, download, or request deletion</li>
              <li><strong>Via Email:</strong> Contact our Data Protection Officer (see below)</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              We will respond to all requests within <strong>30 days</strong>.
            </p>
          </section>

          {/* Data Retention */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Retention</h2>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Active Accounts:</strong> Data retained while subscription is active</li>
              <li><strong>Inactive Accounts:</strong> Data deleted after 12 months of inactivity</li>
              <li><strong>Consent Withdrawal:</strong> Data deleted within 30 days of consent withdrawal</li>
              <li><strong>Legal Requirements:</strong> Some data may be retained longer if required by law</li>
            </ul>
          </section>

          {/* Third-Party Services */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Third-Party Services</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We use the following third-party services to operate our platform:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Twilio:</strong> WhatsApp messaging (processes phone numbers only)</li>
              <li><strong>OpenAI:</strong> Educational content generation (no personal identifiers shared)</li>
              <li><strong>MongoDB:</strong> Secure data storage</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              All third-party services are contractually bound to protect data and comply with applicable laws.
            </p>
          </section>

          {/* Changes to Policy */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Changes to This Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify parents of any 
              material changes via WhatsApp and update the "Last Updated" date. Continued use of 
              our services after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          {/* Contact */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              For any questions, concerns, or to exercise your data rights, please contact:
            </p>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="font-semibold text-gray-800">Contentium Tech</p>
              <p className="text-sm text-gray-600 mb-2">Parent Company of Eduzy</p>
              <div className="flex items-center gap-2 mt-2 text-gray-700">
                <Mail className="w-4 h-4" />
                <a href="mailto:info@contentiumtech.com" className="hover:text-emerald-600">info@contentiumtech.com</a>
              </div>
              <div className="flex items-center gap-2 mt-1 text-gray-700">
                <Phone className="w-4 h-4" />
                <a href="tel:+919654006470" className="hover:text-emerald-600">+91 9654006470</a>
              </div>
            </div>
          </section>

          {/* Grievance */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Grievance Redressal</h2>
            <p className="text-gray-700 leading-relaxed">
              If you are not satisfied with our response to your data request, you may file a 
              complaint with the <strong>Data Protection Board of India</strong> as per the provisions 
              of the DPDP Act, 2023.
            </p>
          </section>

          {/* Footer */}
          <div className="border-t border-gray-200 pt-6 mt-8">
            <p className="text-center text-gray-500 text-sm">
              © 2025 Eduzy by Contentium Tech. All rights reserved.
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <SharedFooter />
    </div>
  );
}
